import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import supabase from '../../lib/supabase';
import { toast } from "sonner";
import Button from "../Common/Button"

const {
  FiGlobe,
  FiSave,
  FiUpload,
  FiImage,
  FiLoader,
  FiSettings,
  FiServer,
  FiAlertTriangle,
  FiMail,
  FiX
} = FiIcons;

// Constants for storage
const STORAGE_BUCKETS = {
  ASSETS: 'assets',
  APP_IMAGES: 'app_images',
  AGENT_IMAGES: 'agent_images'
};

const FILE_PATHS = {
  LOGOS: 'logos',
  AGENTS: 'agents'
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const SettingsPage = ({ setActiveTab }) => {
  const { user, updateUser } = useAuth();
  const [language, setLanguage] = useState(user?.language || 'fr');
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Platform Management States
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [bucketStatus, setBucketStatus] = useState({ checked: false, exists: false });
  
  // Request Email States
  const [defaultRequestEmail, setDefaultRequestEmail] = useState('support@prosomo.com');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  // File input refs
  const logoFileInputRef = useRef(null);
  
  const t = useTranslation(user?.language || 'fr'); // Default to French

  // Check if user is admin
  useEffect(() => {
    if (user && user.role === 'Administrator') {
      setIsAdmin(true);
    }
  }, [user]);

  // Initialize language from user settings
  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
    }
  }, [user]);

  // Check if required buckets exist and create them if needed
  useEffect(() => {
    const checkAndCreateBuckets = async () => {
      if (!isAdmin) return;
      
      try {
        // First, check if buckets exist
        const { data: buckets, error } = await supabase
          .storage
          .listBuckets();

        if (error) {
          console.error('Error checking buckets:', error);
          return;
        }

        const bucketsToCheck = [
          STORAGE_BUCKETS.ASSETS,
          STORAGE_BUCKETS.APP_IMAGES,
          STORAGE_BUCKETS.AGENT_IMAGES
        ];

        const missingBuckets = bucketsToCheck.filter(
          bucketName => !buckets.some(bucket => bucket.name === bucketName)
        );

        // Create missing buckets
        for (const bucketName of missingBuckets) {
          try {
            const { error: createError } = await supabase.storage
              .createBucket(bucketName, { public: true });

            if (createError) {
              console.error(`Error creating bucket ${bucketName}:`, createError);
            } else {
              console.log(`Created bucket: ${bucketName}`);
            }
          } catch (bucketError) {
            console.error(`Failed to create bucket ${bucketName}:`, bucketError);
          }
        }

        setBucketStatus({ checked: true, exists: true });
      } catch (checkError) {
        console.error('Error in bucket check/creation process:', checkError);
        setBucketStatus({ checked: true, exists: false });
      }
    };

    if (isAdmin && !bucketStatus.checked) {
      checkAndCreateBuckets();
    }
  }, [isAdmin, bucketStatus.checked]);

  // Fetch app logo if user is admin
  useEffect(() => {
    if (isAdmin) {
      fetchAppLogo();
      fetchDefaultRequestEmail();
    }
  }, [isAdmin]);

  const fetchAppLogo = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings_a1b2c3d4e5')
        .select('value')
        .eq('key', 'app_logo')
        .single();

      if (error) throw error;
      
      if (data && data.value) {
        setLogoUrl(data.value);
      }
    } catch (error) {
      console.error('Error fetching app logo:', error);
    }
  };

  const fetchDefaultRequestEmail = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings_a1b2c3d4e5')
        .select('value')
        .eq('key', 'default_request_email')
        .single();

      if (!error && data && data.value) {
        setDefaultRequestEmail(data.value);
      }
    } catch (error) {
      console.error('Error fetching default request email:', error);
    }
  };

  const handleSaveLanguage = async () => {
    setLoading(true);
    
    try {
      await updateUser({ language });
      toast.success(t('settings.languageUpdated', 'Language updated successfully!'));
    } catch (error) {
      toast.error(t('settings.languageUpdateError', 'Error updating language'));
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    // Check file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast.error(t('settings.invalidImageFile', 'Please select an image file (PNG, JPG, GIF, or WebP)'));
      return false;
    }

    // Check file size (max 5MB)
    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('settings.imageSizeTooLarge', 'Image must be less than 5MB'));
      return false;
    }

    return true;
  };

  const uploadToStorage = async (file, bucketName, path) => {
    // Generate a unique filename to avoid collisions
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = path ? `${path}/${fileName}` : fileName;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, { cacheControl: '3600', upsert: true });

    if (error) throw error;

    // Get public URL
    const { data: publicUrlData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);

    if (!publicUrlData || !publicUrlData.publicUrl) {
      throw new Error(t('settings.imageUrlError', 'Failed to get public URL for uploaded file'));
    }

    return publicUrlData.publicUrl;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) {
      return;
    }

    setLogoLoading(true);

    try {
      // Upload to the assets bucket under the logos path
      const publicUrl = await uploadToStorage(
        file,
        STORAGE_BUCKETS.ASSETS,
        FILE_PATHS.LOGOS
      );

      // Update in settings table
      const { error: updateError } = await supabase
        .from('app_settings_a1b2c3d4e5')
        .update({ value: publicUrl, updated_at: new Date().toISOString() })
        .eq('key', 'app_logo');

      if (updateError) throw updateError;

      // Update local state with new URL
      setLogoUrl(publicUrl);
      toast.success(t('settings.logoUpdated', 'Logo updated successfully!'));

      // Clear file input
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(t('settings.logoUploadError', 'Error uploading logo'));
    } finally {
      setLogoLoading(false);
    }
  };

  const handleSaveDefaultEmail = async () => {
    setEmailSaving(true);
    
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(defaultRequestEmail)) {
        throw new Error(t('settings.invalidEmail', 'Please enter a valid email address'));
      }
      
      // Update in settings table
      const { error: updateError } = await supabase
        .from('app_settings_a1b2c3d4e5')
        .update({ value: defaultRequestEmail, updated_at: new Date().toISOString() })
        .eq('key', 'default_request_email');

      if (updateError) throw updateError;

      toast.success(t('settings.emailUpdated', 'Default request email updated successfully!'));
      setIsEditingEmail(false);
    } catch (error) {
      console.error('Error updating default email:', error);
      toast.error(t('settings.emailUpdateError', 'Error updating email'));
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-12xl mx-auto space-y-8"
      >
        {/* General Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiSettings} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('settings.generalPreferences', 'General Preferences')}
            </h2>
          </div>

          <div className="space-y-6">
            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-3">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiGlobe} className="w-4 h-4" />
                  <span>{t('settings.language', 'Language')}</span>
                </div>
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="language"
                    value="fr"
                    checked={language === 'fr'}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="text-primary-500 focus:ring-primary-500"
                  />
                  <div className="flex items-center space-x-2">
                    <span className="text-lg dark:text-white">🇫🇷</span>
                    <span className="font-medium text-gray-900 dark:text-white">{t('settings.french', 'French')}</span>
                  </div>
                </label>
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="language"
                    value="en"
                    checked={language === 'en'}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="text-primary-500 focus:ring-primary-500"
                  />
                  <div className="flex items-center space-x-2">
                    <span className="text-lg dark:text-white">🇺🇸</span>
                    <span className="font-medium text-gray-900 dark:text-white">{t('settings.english', 'English')}</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Save Button */}
            {language !== user?.language && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="pt-4 border-t border-gray-200"
              >
                <Button
                  onClick={handleSaveLanguage}
                  disabled={loading}
                  loading={loading}
                  icon={FiSave}                  
                >
                  {t('settings.save', 'Save')}
                </Button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Platform Management - Admin Only */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiServer} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('settings.platformManagement', 'Platform Management')}
              </h2>
            </div>

            {!bucketStatus.exists && bucketStatus.checked && (
              <div className="mb-4 p-3 rounded-lg bg-yellow-50 text-yellow-700 border border-yellow-200 flex items-center space-x-2">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 shrink-0" />
                <span>{t('settings.bucketError', 'Storage buckets are not configured properly. Some image upload features may not work.')}</span>
              </div>
            )}

            <div className="space-y-8">
              {/* App Logo Management */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('settings.appLogo', 'Application Logo')}
                </h3>                
                <div className="flex items-start space-x-6">
                  <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={t('settings.appLogoAlt', 'App Logo')}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://ui-avatars.com/api/?name=AI&background=007ee5&color=fff&size=200&bold=true";
                        }}
                      />
                    ) : (
                      <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('settings.appLogoDescription', 'Update the application logo that appears in the sidebar and throughout the platform. For best results, use a square image (1:1 ratio) with dimensions of at least 200x200 pixels.')}
                    </p>
                    <div className="flex items-center space-x-3">
                      <input
                        type="file"
                        accept="image/*"
                        ref={logoFileInputRef}
                        className="hidden"
                        id="logo-upload"
                        onChange={handleLogoUpload}
                      />
                      <Button
                        htmlFor="logo-upload"
                        onClick={() => logoFileInputRef.current.click()}
                        className="flex space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                        icon={FiUpload}
                        loading={logoLoading}
                        responsive
                      >
                        {t('settings.uploadNewLogo', 'Upload New Logo')}
                      </Button>
                      {logoUrl && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('settings.logoChangeNote', 'Changes will apply immediately after upload')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Default Request Email Setting */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('settings.defaultRequestEmail', 'Default Request Email')}
                </h3>
                
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('settings.defaultRequestEmailDescription', 'Set the default email address where user requests will be sent when no agent-specific email is configured.')}
                  </p>
                  
                  {isEditingEmail ? (
                    <div className="flex items-center space-x-3">
                      <div className="relative flex-1">
                        <SafeIcon 
                          icon={FiMail} 
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                        />
                        <input
                          type="email"
                          value={defaultRequestEmail}
                          onChange={(e) => setDefaultRequestEmail(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder={t('settings.emailPlaceholder', 'support@example.com')}
                        />
                      </div>
                      <Button
                        onClick={handleSaveDefaultEmail}
                        disabled={emailSaving}
                        loading={emailSaving}
                        icon={FiSave}
                        responsive
                      >
                        {t('settings.save', 'Save')}
                      </Button>
                      <Button
                        onClick={() => setIsEditingEmail(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors dark:text-white"
                        responsive
                        icon={FiX}
                        iconClassName="lg:hidden"
                      >
                        {t('settings.cancel', 'Cancel')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-750">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-500" />
                        <span className="font-medium text-gray-900 dark:text-black">
                          {defaultRequestEmail}
                        </span>
                      </div>
                      <Button
                        onClick={() => setIsEditingEmail(true)}
                        className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xs hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        {t('settings.edit', 'Edit')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Note about AI Agent Images */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('settings.aiAgentImages', 'AI Agent Images')}
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <p className="text-blue-700 dark:text-blue-300 text-sm" dangerouslySetInnerHTML={{ __html: t('settings.aiAgentImagesNote', '💡 <strong>AI Agent images are now managed in the AI Agents Management section.</strong><br />Go to the "AI Agents" tab to upload and manage images for each individual agent.') }  } />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* App Info */}
        {/* <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {t('settings.appInfo', 'About the Application')}
          </h3>
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>{t('settings.version', 'Version: 1.0.0')}</p>
            <p>{t('settings.platformDescription', 'Internal AI Platform for Employees')}</p>
            <p>{t('settings.developedWith', 'Developed with React & Vite')}</p>
          </div>
        </div> */}
      </motion.div>
    </div>
  );
};

export default SettingsPage;